import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { 
  CheckCircle, XCircle, Clock, AlertCircle, X, RefreshCw, FileText,
  Camera, Star, MessageSquare, DollarSign, Calendar, User, Sparkles,
  ThumbsUp, ThumbsDown, ChevronRight, Award, Shield, Send
} from 'lucide-react';

export default function CompletionApproval({ job, onClose, onApproved }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [aiReview, setAiReview] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  
  const [approval, setApproval] = useState({
    status: 'approved', // approved, partial, revision_requested, rejected
    approved_amount: 0,
    rating: 5,
    review: '',
    revision_notes: ''
  });

  useEffect(() => {
    if (job) {
      loadCompletionData();
    }
  }, [job]);

  const loadCompletionData = async () => {
    setLoading(true);
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/completion`);
      setCompletionData(response.completion);
      setApproval(prev => ({
        ...prev,
        approved_amount: response.completion?.total_amount || job?.total_pay || 0
      }));
      
      // Load checklist based on job type
      const checklistResponse = await apiCall(`/api/employer/jobs/${job._id}/checklist`);
      setChecklist(checklistResponse.items || getDefaultChecklist());
    } catch (error) {
      console.error('Failed to load completion data:', error);
      setChecklist(getDefaultChecklist());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultChecklist = () => [
    { id: 1, label: 'Worker arrived on time', checked: false },
    { id: 2, label: 'Work completed as described', checked: false },
    { id: 3, label: 'Quality meets expectations', checked: false },
    { id: 4, label: 'Worker followed safety protocols', checked: false },
    { id: 5, label: 'No issues or incidents reported', checked: false }
  ];

  const loadAIReview = async () => {
    setLoadingAI(true);
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/ai-review`, {
        method: 'POST',
        body: JSON.stringify({
          completion_data: completionData,
          checklist: checklist
        })
      });
      setAiReview(response.review);
    } catch (error) {
      toast.error('Failed to generate AI review');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleChecklistToggle = (itemId) => {
    setChecklist(prev => prev.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
  };

  const handleSubmitApproval = async () => {
    if (approval.status === 'revision_requested' && !approval.revision_notes.trim()) {
      toast.error('Please provide revision notes');
      return;
    }

    setSubmitting(true);
    try {
      await apiCall(`/api/employer/jobs/${job._id}/approve`, {
        method: 'POST',
        body: JSON.stringify({
          ...approval,
          checklist: checklist
        })
      });
      
      toast.success(
        approval.status === 'approved' 
          ? 'Job approved! Payment will be released.' 
          : approval.status === 'partial'
          ? 'Partial approval submitted.'
          : 'Revision request sent to worker.'
      );
      
      onApproved?.(approval);
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to submit approval');
    } finally {
      setSubmitting(false);
    }
  };

  const getCompletionScore = () => {
    const checkedCount = checklist.filter(item => item.checked).length;
    return Math.round((checkedCount / checklist.length) * 100);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <CheckCircle className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Completion & Approval</h2>
                <p className="text-white/80">{job?.title}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="flex gap-4 mt-4">
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Hours Worked</span>
              <p className="text-xl font-bold">{completionData?.hours_worked || '-'}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Total Amount</span>
              <p className="text-xl font-bold">${completionData?.total_amount?.toFixed(2) || job?.total_pay?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Completion Score</span>
              <p className="text-xl font-bold">{getCompletionScore()}%</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column - Checklist & Worker Info */}
              <div className="space-y-6">
                {/* Verification Checklist */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Verification Checklist
                  </h3>
                  
                  <div className="space-y-3">
                    {checklist.map((item) => (
                      <label 
                        key={item.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                          item.checked 
                            ? 'bg-emerald-50 border border-emerald-200' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => handleChecklistToggle(item.id)}
                          className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className={`text-sm ${item.checked ? 'text-emerald-700' : 'text-gray-700'}`}>
                          {item.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Worker Performance */}
                {completionData?.worker && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-emerald-600" />
                      Worker Details
                    </h3>
                    
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <span className="text-xl font-bold text-white">
                          {completionData.worker.full_name?.charAt(0) || 'W'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{completionData.worker.full_name}</p>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            {completionData.worker.rating?.toFixed(1) || 'New'}
                          </span>
                          <span>{completionData.worker.jobs_completed || 0} jobs</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600 font-medium">Check In</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {completionData.check_in_time 
                            ? new Date(completionData.check_in_time).toLocaleTimeString() 
                            : '-'}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-600 font-medium">Check Out</p>
                        <p className="text-sm font-semibold text-blue-900">
                          {completionData.check_out_time 
                            ? new Date(completionData.check_out_time).toLocaleTimeString() 
                            : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Photos/Evidence */}
                {completionData?.photos?.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Camera className="w-5 h-5 text-emerald-600" />
                      Completion Photos
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      {completionData.photos.map((photo, idx) => (
                        <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Approval Form & AI Review */}
              <div className="space-y-6">
                {/* AI Review */}
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-violet-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      AI Review Assistant
                    </h3>
                    <button
                      onClick={loadAIReview}
                      disabled={loadingAI}
                      className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition disabled:opacity-50"
                    >
                      {loadingAI ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Analyze'}
                    </button>
                  </div>

                  {aiReview ? (
                    <div className="space-y-3">
                      <div className={`p-3 rounded-lg flex items-start gap-3 ${
                        aiReview.recommendation === 'approve' 
                          ? 'bg-emerald-100' 
                          : aiReview.recommendation === 'review' 
                          ? 'bg-amber-100' 
                          : 'bg-red-100'
                      }`}>
                        {aiReview.recommendation === 'approve' ? (
                          <ThumbsUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                        ) : aiReview.recommendation === 'review' ? (
                          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        ) : (
                          <ThumbsDown className="w-5 h-5 text-red-600 mt-0.5" />
                        )}
                        <div>
                          <p className={`text-sm font-medium ${
                            aiReview.recommendation === 'approve' 
                              ? 'text-emerald-900' 
                              : aiReview.recommendation === 'review' 
                              ? 'text-amber-900' 
                              : 'text-red-900'
                          }`}>
                            {aiReview.recommendation === 'approve' 
                              ? 'Recommended for Approval' 
                              : aiReview.recommendation === 'review'
                              ? 'Needs Review'
                              : 'Issues Detected'}
                          </p>
                          <p className={`text-sm ${
                            aiReview.recommendation === 'approve' 
                              ? 'text-emerald-700' 
                              : aiReview.recommendation === 'review' 
                              ? 'text-amber-700' 
                              : 'text-red-700'
                          }`}>
                            {aiReview.summary}
                          </p>
                        </div>
                      </div>
                      
                      {aiReview.concerns && aiReview.concerns.length > 0 && (
                        <div className="p-3 bg-white rounded-lg border border-violet-200">
                          <p className="text-sm font-medium text-violet-900 mb-2">Concerns:</p>
                          <ul className="text-sm text-violet-700 list-disc list-inside">
                            {aiReview.concerns.map((concern, idx) => (
                              <li key={idx}>{concern}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-white/50 rounded-lg">
                      <Sparkles className="w-10 h-10 text-violet-300 mx-auto mb-2" />
                      <p className="text-violet-600 text-sm">Click "Analyze" for AI-powered review</p>
                    </div>
                  )}
                </div>

                {/* Approval Form */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Approval Decision</h3>
                  
                  {/* Approval Status */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {[
                      { value: 'approved', label: 'Full Approval', icon: <CheckCircle className="w-5 h-5" />, color: 'emerald' },
                      { value: 'partial', label: 'Partial Approval', icon: <AlertCircle className="w-5 h-5" />, color: 'amber' },
                      { value: 'revision_requested', label: 'Request Revision', icon: <MessageSquare className="w-5 h-5" />, color: 'blue' },
                      { value: 'rejected', label: 'Reject', icon: <XCircle className="w-5 h-5" />, color: 'red' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setApproval({ ...approval, status: option.value })}
                        className={`p-3 rounded-lg border-2 text-left transition ${
                          approval.status === option.value
                            ? `border-${option.color}-500 bg-${option.color}-50`
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`flex items-center gap-2 text-sm font-medium ${
                          approval.status === option.value ? `text-${option.color}-700` : 'text-gray-700'
                        }`}>
                          {option.icon}
                          {option.label}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Approved Amount (for partial) */}
                  {approval.status === 'partial' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Approved Amount</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={approval.approved_amount}
                          onChange={(e) => setApproval({ ...approval, approved_amount: parseFloat(e.target.value) || 0 })}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                          max={completionData?.total_amount || job?.total_pay}
                        />
                      </div>
                    </div>
                  )}

                  {/* Revision Notes */}
                  {approval.status === 'revision_requested' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Revision Notes *</label>
                      <textarea
                        value={approval.revision_notes}
                        onChange={(e) => setApproval({ ...approval, revision_notes: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={3}
                        placeholder="Explain what needs to be revised..."
                      />
                    </div>
                  )}

                  {/* Rating */}
                  {(approval.status === 'approved' || approval.status === 'partial') && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rate Worker</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setApproval({ ...approval, rating: star })}
                            className="p-1 transition"
                          >
                            <Star 
                              className={`w-8 h-8 ${
                                star <= approval.rating 
                                  ? 'text-amber-500 fill-amber-500' 
                                  : 'text-gray-300'
                              }`} 
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Review */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Review (Optional)</label>
                    <textarea
                      value={approval.review}
                      onChange={(e) => setApproval({ ...approval, review: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 resize-none"
                      rows={3}
                      placeholder="Write a review for this worker..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    onClick={handleSubmitApproval}
                    disabled={submitting}
                    className={`w-full py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2 ${
                      approval.status === 'approved'
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg'
                        : approval.status === 'partial'
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg'
                        : approval.status === 'revision_requested'
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:shadow-lg'
                        : 'bg-gradient-to-r from-red-500 to-pink-500 text-white hover:shadow-lg'
                    }`}
                  >
                    {submitting ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    {approval.status === 'approved' ? 'Approve & Release Payment' :
                     approval.status === 'partial' ? 'Submit Partial Approval' :
                     approval.status === 'revision_requested' ? 'Request Revision' :
                     'Reject Job'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
